import { useReactFlow } from '@xyflow/react';
import { useCallback, useState } from 'react';

import { useAutomationService } from '@/src/infrastructure/Protocol/Automation/useAutomationService';
import { SOURCE_HANDLE_YES } from '@/src/modules/AutomationModuleNew/constants/flows';
import {
  ActionNodeData,
  BinaryConditionResultConnectionConditionType,
  ConditionalNodeData,
  JourneyConnectionType,
  JourneySchema,
  TriggerNodeData,
} from '@/src/modules/AutomationModuleNew/types/flowsTypes';
import { findRootNodeId } from '@/src/modules/AutomationModuleNew/utils/journeysHelper';

export const useUpdateJourney = () => {
  const automationService = useAutomationService();
  const [isLoading, setIsLoading] = useState(false);
  const { getNodes, getEdges } = useReactFlow();

  const updateJourney = useCallback(
    async (journeyId: number) => {
      if (!automationService) return;

      const nodes = getNodes();
      const edges = getEdges();
      const journeySchema: JourneySchema = {
        root: '',
        nodes: nodes.map((node) => {
          const nodeData = node.data as ConditionalNodeData | TriggerNodeData | ActionNodeData;
          return { ...nodeData, position: node.position } as
            | ConditionalNodeData
            | TriggerNodeData
            | ActionNodeData;
        }),
        connections: edges.map((edge) => {
          const isBinaryConditional = !!edge.sourceHandle;
          let condition: BinaryConditionResultConnectionConditionType | undefined = undefined;
          if (isBinaryConditional) {
            condition = {
              type: 'binaryConditional',
              value: edge.sourceHandle === SOURCE_HANDLE_YES ? true : false,
            } as BinaryConditionResultConnectionConditionType;
          }
          return {
            id: edge.id,
            from: edge.source,
            to: edge.target,
            condition,
          } as JourneyConnectionType;
        }),
      };
      const rootId = findRootNodeId(nodes, journeySchema.connections) ?? 'node1';
      journeySchema.root = rootId;

      setIsLoading(true);
      const response = await automationService.updateJourneySchema(journeyId, journeySchema);
      setIsLoading(false);
      return response;
    },
    [automationService, getEdges, getNodes]
  );

  return { isLoading, updateJourney };
};
